import { Component, computed, effect, input, OnDestroy, signal, untracked } from '@angular/core';

import { bookingTypeName } from '../types/bookingType';
import { CalendarField, CalendarPeriod, CalendarViewType } from './school-calendar.types';
import { PagePieComponent } from './page-pie.component';
import { lockerRoomText } from './locker-room-text';
import { buildDayLayout, buildWeekLayout, CalendarLayout, paginate } from './calendar-layout';

/**
 * Columns per page: seven for the week, so a whole week always fits on one page; five for the
 * day, so a resource's name has room ("Forsamlingshus 1", "Badmintonbane 2").
 */
const WEEK_COLUMNS_PER_PAGE = 7;
const DAY_COLUMNS_PER_PAGE = 5;

/** More hours than this on the axis (a booking across midnight gives all 24): a label every other hour. */
const ALL_LABELS_UP_TO_HOURS = 14;

/** This many bookings side by side or more: each box shows just its start and its activity. */
export const NARROW_FROM_LANES = 3;

/**
 * The school-timetable views: the week with weekdays as columns, or today with the resources as
 * columns. Time runs down the side; each booking is a block at its time. More columns than fit
 * are shown a page at a time, turning every `pageSeconds`.
 */
@Component({
  selector: 'app-calendar-view',
  imports: [PagePieComponent],
  templateUrl: './calendar-view.component.html',
  styleUrl: './calendar-view.component.scss',
})
export class CalendarViewComponent implements OnDestroy {
  readonly periods = input.required<CalendarPeriod[]>();
  /** `WeekCalendar` for the week; anything else is the day (the module passes its own view type). */
  readonly viewType = input.required<CalendarViewType | string>();
  readonly from = input.required<string>();
  readonly now = input.required<Date>();
  readonly pageSeconds = input(20);
  /** The lines in each box, in order. */
  readonly cardFields = input.required<CalendarField[]>();

  readonly lockerRoomText = lockerRoomText;

  /** With both the activity and the level chosen, the level follows the activity ("Fodbold · U12"). */
  readonly levelInline = computed(() => this.cardFields().includes('Activity') && this.cardFields().includes('Level'));
  readonly pageSecondsClamped = computed(() => Math.max(5, this.pageSeconds()));

  readonly layout = computed<CalendarLayout>(() =>
    this.viewType() === 'WeekCalendar'
      ? buildWeekLayout(this.periods(), new Date(this.from()), this.now())
      : buildDayLayout(this.periods(), new Date(this.from()), this.now()),
  );

  readonly pages = computed(() =>
    paginate(this.layout().columns, this.viewType() === 'WeekCalendar' ? WEEK_COLUMNS_PER_PAGE : DAY_COLUMNS_PER_PAGE),
  );
  /** A number, not the pages: the minute refresh rebuilds the pages but must not restart the cycle. */
  private readonly pageCount = computed(() => this.pages().length);
  readonly pageIndex = signal(0);
  readonly page = computed(() => this.pages()[this.pageIndex() % this.pages().length]);

  readonly hours = computed(() => {
    const { startHour, endHour } = this.layout();
    return Array.from({ length: endHour - startHour }, (_, i) => startHour + i);
  });

  /** Label every hour, or — with many hours on the axis — every other, so the labels never crowd. */
  readonly labelEvery = computed(() => (this.hours().length > ALL_LABELS_UP_TO_HOURS ? 2 : 1));

  readonly narrowFromLanes = NARROW_FROM_LANES;

  /** Where "now" is on the grid, 0–1, or null when it is outside the hours shown. */
  readonly nowLine = computed(() => {
    const { startHour, endHour } = this.layout();
    const now = this.now();
    const hours = now.getHours() + now.getMinutes() / 60;
    return hours < startHour || hours > endHour ? null : (hours - startHour) / (endHour - startHour);
  });

  private pageTimer?: ReturnType<typeof setInterval>;

  constructor() {
    // Restart the page cycle when the number of pages or the interval changes.
    effect(() => {
      const pageCount = this.pageCount();
      const seconds = this.pageSecondsClamped();

      clearInterval(this.pageTimer);
      if (untracked(this.pageIndex) >= pageCount) this.pageIndex.set(0);
      if (pageCount > 1) {
        this.pageTimer = setInterval(() => this.pageIndex.update((i) => (i + 1) % pageCount), seconds * 1000);
      }
    });
  }

  ngOnDestroy() {
    clearInterval(this.pageTimer);
  }

  isBlocked(period: CalendarPeriod): boolean {
    return bookingTypeName(period.bookingType).startsWith('BLOCKED');
  }

  hourLabel(hour: number): string {
    return `${String(hour).padStart(2, '0')}:00`;
  }
}
